import axios from 'axios';

const BACKEND_URL = '';
const API = '/api';

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 0, // No timeout - pages, downloads, uploads must never fail due to timeout
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle auth errors gracefully
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect to login for 401 errors on protected routes
    // Don't redirect for login endpoints themselves
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      // Don't clear auth for login requests - let them show error message
      if (!requestUrl.includes('/auth/')) {
        // For other requests, the token might be expired
        // Don't auto-logout, just let the error propagate
        console.warn('Auth error on protected route:', requestUrl);
      }
    }
    return Promise.reject(error);
  }
);

// Utility function for safe file downloads with proper error handling
export const downloadFile = async (apiCall, filename, onSuccess, onError) => {
  try {
    const response = await apiCall;
    
    // Check if response is valid blob data
    if (!response?.data) {
      throw new Error('No data received from server');
    }
    
    // Check if the response is an error JSON (sometimes server returns JSON error with 200)
    if (response.data instanceof Blob && response.data.type === 'application/json') {
      const text = await response.data.text();
      try {
        const jsonError = JSON.parse(text);
        if (jsonError.detail || jsonError.error) {
          throw new Error(jsonError.detail || jsonError.error);
        }
      } catch (e) {
        // Not JSON, proceed with download
      }
    }
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    if (onSuccess) onSuccess();
    return { success: true };
  } catch (error) {
    console.error('Download error:', error);
    
    let errorMessage = 'Download failed';
    
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const jsonError = JSON.parse(text);
        errorMessage = jsonError.detail || jsonError.error || errorMessage;
      } catch (e) {
        // Could not parse error
      }
    } else if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    if (onError) onError(errorMessage);
    return { success: false, error: errorMessage };
  }
};

// Utility function for file uploads with proper error handling
export const uploadFile = async (apiCall, onSuccess, onError) => {
  try {
    const response = await apiCall;
    const data = response?.data || response || {};
    
    // Check for success indicators
    if (data.success === true || data.message || data.marked_count !== undefined) {
      if (onSuccess) onSuccess(data);
      return { success: true, data };
    }
    
    // If we got here with a response, assume success
    if (onSuccess) onSuccess(data);
    return { success: true, data };
  } catch (error) {
    console.error('Upload error:', error);
    
    // Check if the error response actually contains success data
    if (error.response?.data?.success === true) {
      if (onSuccess) onSuccess(error.response.data);
      return { success: true, data: error.response.data };
    }
    
    let errorMessage = 'Upload failed';
    
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      errorMessage = 'Upload timed out - data may have been saved. Please refresh.';
    } else if (!error.response) {
      errorMessage = 'Network error - please check your connection.';
    } else if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    if (onError) onError(errorMessage);
    return { success: false, error: errorMessage };
  }
};

// Supervisor APIs (renamed from inspectorAPI)
export const supervisorAPI = {
  login: (supervisor_id, pin) => api.post('/auth/supervisor/login', { supervisor_id, pin }),
  getAssignedParks: (supervisor_id) => api.get(`/supervisor/assigned-parks/${supervisor_id}`),
  getOTParks: (supervisor_id) => api.get(`/supervisor/ot-parks/${supervisor_id}`),
  getCurrentVisitOrder: (supervisor_id) => api.get(`/supervisor/current-visit-order/${supervisor_id}`),
  startInspection: (data) => api.post('/inspection/start', data),
  uploadPhoto: (data) => api.post('/inspection/upload-photo', data, { 
    timeout: 0, // No timeout for photo uploads
    maxContentLength: Infinity,
    maxBodyLength: Infinity
  }),
  submitInspection: (data) => api.post('/inspection/submit', data),
  getInspection: (inspection_id) => api.get(`/inspection/${inspection_id}`),
  getInspectionPhotos: (inspection_id) => api.get(`/inspection/${inspection_id}/photos`),
  editInspection: (inspection_id, data) => api.put(`/inspection/${inspection_id}/edit`, data),
  changePin: (data) => api.post('/supervisor/change-pin', data),
  getCalendarData: (supervisor_id, year, month) => 
    api.get(`/supervisor/calendar/${supervisor_id}`, { params: { year, month } }),
  getWorkerCalendar: (supervisor_id, month) => 
    api.get(`/supervisor/worker-calendar/${supervisor_id}`, { params: { month } }),
  // Dynamic Questionnaires
  getActiveQuestionnaires: () => api.get('/supervisor/active-questionnaires'),
  submitQuestionnaireResponses: (data) => api.post('/supervisor/questionnaire-responses', data),
  // Pending Attendance (Absence Correction)
  getPendingAttendance: (supervisorId, month) => api.get(`/supervisor/pending-attendance/${supervisorId}`, { params: { month } }),
  correctAttendance: (formData) => api.post('/supervisor/correct-attendance', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 0
  }),
  requestCorrectionPermission: (supervisorId, requestedDate) => api.post('/supervisor/request-correction-permission', {
    supervisor_id: supervisorId,
    requested_date: requestedDate
  }),
};

// Keep backward compatibility alias
export const inspectorAPI = supervisorAPI;

export const adminAPI = {
  login: (username, password) => api.post('/auth/admin/login', { username, password }),
  getDashboardSummary: () => api.get('/admin/dashboard/summary'),
  getZoneAttendance: (date) => api.get('/admin/reports/zone-attendance', { params: { date_filter: date } }),
  verifyZoneAttendancePark: (date, park_id, verified = true) => 
    api.post('/admin/reports/zone-attendance/verify', { date, park_id, verified }),
  
  // Admin cancel supervisor correction
  cancelSupervisorCorrection: (attendance_id, reason) => 
    api.post('/admin/cancel-supervisor-correction', { attendance_id, reason }),
  getCancelledCorrections: (date, supervisor, zone, month) => 
    api.get('/admin/cancelled-corrections', { params: { date_filter: date, supervisor_filter: supervisor, zone_filter: zone, month } }),
  
  getClosedParks: (date) => api.get('/admin/reports/closed-parks', { params: { date_filter: date } }),
  getCleanlinessReport: (date) => api.get('/admin/reports/cleanliness', { params: { date_filter: date } }),
  
  // Employee Master
  getEmployeeMaster: (zone, category) => api.get('/admin/reports/employee-master', { params: { zone, category } }),
  downloadEmployeeMaster: (zone, category) => api.get('/admin/reports/employee-master/download', { 
    params: { zone, category },
    responseType: 'blob'
  }),
  
  getSupervisors: () => api.get('/admin/data/supervisors'),
  getInspectors: () => api.get('/admin/data/supervisors'), // alias for backward compatibility
  createSupervisor: (data) => api.post('/admin/data/supervisor', data),
  createInspector: (data) => api.post('/admin/data/supervisor', data), // alias
  updateSupervisor: (supervisor_id, data) => api.put(`/admin/data/supervisor/${supervisor_id}`, data),
  deleteSupervisor: (supervisor_id) => api.delete(`/admin/data/supervisor/${supervisor_id}`),
  deleteInspector: (supervisor_id) => api.delete(`/admin/data/supervisor/${supervisor_id}`), // alias
  getParks: () => api.get('/admin/data/parks'),
  createPark: (data) => api.post('/admin/data/park', data),
  updatePark: (park_id, data) => api.put(`/admin/data/park/${park_id}`, data),
  getParkDetails: (park_id) => api.get(`/admin/data/park/${park_id}`),
  updateParkEmployees: (park_id, employees) => api.put(`/admin/data/park/${park_id}/employees`, employees),
  toggleParkStatus: (park_id) => api.post(`/admin/data/park/${park_id}/toggle-status`),
  deletePark: (park_id) => api.delete(`/admin/data/park/${park_id}`),
  
  // Clear all data (async with progress)
  clearAllData: () => api.delete('/admin/data/clear-all'),
  clearSpecificData: (dataType) => api.delete(`/admin/data/clear/${dataType}`),
  getClearProgress: (jobId) => api.get(`/admin/data/clear-progress/${jobId}`),
  
  // Bulk assignment
  bulkAssignParks: (park_ids, supervisor_id) => api.post('/admin/data/bulk-assign-parks', { park_ids, supervisor_id }),
  
  // Visit Order Combinations
  getVisitCombinations: (supervisor_id) => api.get(`/admin/supervisor/${supervisor_id}/visit-combinations`),
  saveVisitCombinations: (supervisor_id, data) => api.post(`/admin/supervisor/${supervisor_id}/visit-combinations`, data),
  setVisitOrderOverride: (supervisor_id, combo_index) => api.post(`/admin/supervisor/${supervisor_id}/visit-combination/override`, { combo_index }),
  setMasterVisitOrderToggle: (enabled) => api.post('/admin/settings/master-visit-order', { enabled }),
  getMasterVisitOrderToggle: () => api.get('/admin/settings/master-visit-order'),
  
  // Password management
  resetSupervisorPassword: (supervisor_id, new_pin) => api.post(`/admin/data/supervisor/${supervisor_id}/reset-password`, { new_pin }),
  changeAdminPassword: (current_password, new_password) => api.post('/admin/change-password', { current_password, new_password }),
  
  // Supervisor monitoring & attendance
  getSupervisorMonitoring: (date, supervisor_id) => api.get('/admin/supervisor-monitoring', { params: { date_filter: date, supervisor_id } }),
  getSupervisorAttendance: (date, month) => api.get('/admin/reports/supervisor-attendance', { params: { date_filter: date, month } }),
  
  // No manpower parks
  getNoManpowerParks: (date) => api.get('/admin/reports/no-manpower', { params: { date_filter: date } }),
  downloadNoManpowerReport: (date) => api.get('/admin/reports/no-manpower/download', { params: { date_filter: date }, responseType: 'blob' }),
  
  // Calendar APIs
  getZones: () => api.get('/admin/calendar/zones'),
  getDivisions: (zone) => api.get('/admin/calendar/divisions', { params: { zone } }),
  getParksList: (zone, division) => api.get('/admin/calendar/parks', { params: { zone, division } }),
  getCalendarData: (zone, division, park_id, year, month) => 
    api.get('/admin/calendar/data', { params: { zone, division, park_id, year, month } }),
  getDateDetails: (park_id, date) => api.get('/admin/calendar/date-details', { params: { park_id, date } }),
  
  // Park details with manpower
  getParkDetails: (park_id) => api.get(`/admin/data/park/${park_id}`),
  
  // Park photos for a specific date
  getParkPhotos: (park_id, date) => api.get(`/admin/park/${park_id}/photos`, { params: { date_filter: date } }),
  
  // Attendance by date (without inspection)
  getAttendanceByDate: (park_id, date) => api.get('/admin/attendance/by-date', { params: { park_id, date } }),
  
  // Supervisor details
  getSupervisorDetails: (supervisor_id) => api.get(`/admin/supervisor/${supervisor_id}`),
  
  // Salary configuration
  getSalaryRates: () => api.get('/admin/config/salary-rates'),
  setSalaryRates: (rates) => api.post('/admin/config/salary-rates', rates),
  
  // Monthly Zone Attendance Report
  getMonthlyZoneAttendance: (zone, month) => api.get('/admin/reports/monthly-zone-attendance', { params: { zone, month }, timeout: 0 }),
  downloadMonthlyZoneAttendance: (zone, month) => api.get('/admin/reports/monthly-zone-attendance/download', { 
    params: { zone, month },
    responseType: 'blob',
    timeout: 0 // No timeout for large downloads
  }),
  getAttendanceDiscrepancies: (zone, month) => api.get('/admin/reports/attendance-discrepancies', { 
    params: { zone, month }, 
    timeout: 0 
  }),
  getPendingWorkerAttendance: (supervisorId, month) => api.get('/admin/pending-worker-attendance', { 
    params: { supervisor_id: supervisorId, month }, 
    timeout: 0 
  }),
  
  // Pending Attendance (Not Visited Parks)
  getNotVisitedParks: (date, zone) => api.get('/admin/pending-attendance', { params: { date, zone } }),
  downloadNotVisitedTemplate: (date, zone) => api.get('/admin/pending-attendance/download', { 
    params: { date, zone },
    responseType: 'blob'
  }),
  uploadNotVisitedAttendance: (formData) => api.post('/admin/pending-attendance/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 0 // No timeout for large file uploads
  }),
  
  // Temp Worker Report
  getTempWorkerReport: (date) => api.get('/admin/reports/temp-workers', { params: { date_filter: date } }),
  
  // Pending Parks Report
  getPendingParks: (date) => api.get('/admin/reports/pending-parks', { params: { date_filter: date } }),
  
  // Performance APIs
  getSupervisorPerformance: (date) => api.get('/admin/performance/supervisors', { params: { date } }),
  getInspectorPerformance: (date) => api.get('/admin/performance/supervisors', { params: { date } }), // alias
  getZonePerformance: (date) => api.get('/admin/performance/zones', { params: { date } }),
  
  // Bulk upload APIs
  uploadParks: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/bulk/upload-parks', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0 // No timeout for large file uploads
    });
  },
  getUploadProgress: (uploadId) => api.get(`/admin/bulk/upload-progress/${uploadId}`),
  refreshDataCache: () => api.post('/admin/data/refresh-cache'),
  uploadEmployees: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/bulk/upload-employees', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0 // No timeout for large file uploads
    });
  },
  downloadParksTemplate: () => api.get('/admin/bulk/template-parks', { responseType: 'blob' }),
  downloadEmployeesTemplate: () => api.get('/admin/bulk/template-employees', { responseType: 'blob' }),
  
  // Attendance editing
  editAttendance: (data) => api.put('/admin/attendance/edit', data),
  getAttendanceAuditLog: (date) => api.get('/admin/attendance/audit-log', { params: { date_filter: date } }),
  
  // Bulk Attendance Edit - Download/Upload
  downloadBulkAttendanceTemplate: (startDate, endDate, zone) => api.get('/admin/bulk-attendance/download', {
    params: { start_date: startDate, end_date: endDate, zone },
    responseType: 'blob',
    timeout: 0 // No timeout for large downloads
  }),
  uploadBulkAttendance: (formData) => api.post('/admin/bulk-attendance/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 0 // No timeout for large uploads
  }),
  
  // Storage management
  getStorageSummary: () => api.get('/admin/storage/summary'),
  getStorageDateDetails: (date) => api.get('/admin/storage/date-details', { params: { date } }),
  deletePhotos: (date_str, inspection_ids, photo_urls) => api.delete('/admin/storage/delete-photos', { 
    params: { date_str }, 
    data: { inspection_ids, photo_urls } 
  }),
  verifyStoragePassword: (password) => api.post('/admin/storage/verify-password', { password }),
  
  // Protected pages password verification
  verifyProtectedPassword: (password) => api.post('/admin/verify-protected-password', { password }),
  getRecycleBin: () => api.get('/admin/storage/recycle-bin'),
  recoverFromRecycleBin: (item_ids) => api.post('/admin/storage/recycle-bin/recover', { item_ids }),
  emptyRecycleBin: () => api.delete('/admin/storage/recycle-bin/empty'),
  cleanupRecycleBin: () => api.delete('/admin/storage/recycle-bin/cleanup'),
  
  // GPS monitoring
  getGpsStatus: (date) => api.get('/admin/supervisor-monitoring/gps-status', { params: { date_filter: date } }),
  getGpsRange: () => api.get('/admin/settings/gps-range'),
  setGpsRange: (range_meters) => api.put('/admin/settings/gps-range', { range_meters }),
  
  // Supervisor Attendance Calendar
  getSupervisorAttendanceCalendar: (month) => api.get('/admin/supervisor-attendance-calendar', { params: { month }, timeout: 0 }),
  downloadSupervisorAttendanceExcel: (month) => api.get('/admin/supervisor-attendance-calendar/download', {
    params: { month },
    responseType: 'blob',
    timeout: 0 // No timeout for large downloads
  }),
  
  // Worker Attendance Calendar Download
  downloadWorkerAttendanceExcel: (month, zone, supervisor) => api.get('/admin/worker-attendance-calendar/download', {
    params: { month, zone, supervisor },
    responseType: 'blob',
    timeout: 0 // No timeout for large downloads
  }),
  
  // Background Download APIs (Fallback for large downloads)
  startWorkerAttendanceBackgroundDownload: (month, zone, supervisor) => api.post('/admin/worker-attendance-calendar/download-background', null, {
    params: { month, zone, supervisor }
  }),
  startMonthlyZoneReportBackgroundDownload: (month, zone) => api.post('/admin/reports/monthly-zone-attendance/download-background', null, {
    params: { month, zone }
  }),
  checkDownloadStatus: (taskId) => api.get(`/admin/download-status/${taskId}`),
  downloadBackgroundFile: (taskId) => api.get(`/admin/download-file/${taskId}`, {
    responseType: 'blob'
  }),
  
  // Zone Staff WhatsApp Performance (separate for electrician/supervisor)
  getZoneStaffWhatsAppPerformance: (month, category, region) => api.get('/admin/zone-staff-whatsapp-performance', { 
    params: { month, category, region },
    timeout: 0
  }),
  getZoneStaffWhatsAppDaily: (date, category, region) => api.get('/admin/zone-staff-whatsapp-daily', { 
    params: { date, category, region }
  }),
  getZoneStaffWhatsAppMessages: (date, employeeId) => api.get('/admin/zone-staff-whatsapp-messages', { 
    params: { date, employee_id: employeeId }
  }),
  
  // Survey Questions Management
  getSurveyQuestions: (includeInactive = false) => api.get('/admin/survey-questions', { params: { include_inactive: includeInactive } }),
  getActiveSurveyQuestions: () => api.get('/admin/survey-questions/active'),
  createSurveyQuestion: (question) => api.post('/admin/survey-questions', question),
  updateSurveyQuestion: (questionId, question) => api.put(`/admin/survey-questions/${questionId}`, question),
  deleteSurveyQuestion: (questionId) => api.delete(`/admin/survey-questions/${questionId}`),
  reorderSurveyQuestion: (questionId, newOrder) => api.put(`/admin/survey-questions/${questionId}/reorder`, null, { params: { new_order: newOrder } }),
  
  // Manual Supervisor Attendance
  markSupervisorAttendanceManual: (data) => api.post('/admin/supervisor-attendance/manual', data),
  getManualSupervisorAttendance: (date, supervisorId) => api.get('/admin/supervisor-attendance/manual', { params: { date_filter: date, supervisor_id: supervisorId } }),
  
  // Overtime Tracking
  assignOvertime: (data) => api.post('/admin/overtime/assign', data),
  getOvertimeAssignments: (supervisorId, dateFilter, month, upcoming) => api.get('/admin/overtime/list', { params: { supervisor_id: supervisorId, date_filter: dateFilter, month, upcoming }, timeout: 0 }),
  updateOvertimeAssignment: (assignmentId, data) => api.put(`/admin/overtime/${assignmentId}`, data),
  deleteOvertimeAssignment: (assignmentId) => api.delete(`/admin/overtime/${assignmentId}`),
  getSupervisorOvertimeCalendar: (supervisorId, month) => api.get(`/admin/overtime/calendar/${supervisorId}`, { params: { month } }),
  getAbsentSupervisorsForDate: (date) => api.get('/admin/overtime/absent-supervisors', { params: { date_filter: date } }),
  getSupervisorParksForOT: (supervisorId, date) => api.get(`/admin/overtime/supervisor-parks/${supervisorId}`, { params: { date_filter: date } }),
  
  // Temporary Survey Reports
  getTemporarySurveyReports: (startDate, endDate, zone) => api.get('/admin/survey-reports/temporary', { params: { start_date: startDate, end_date: endDate, zone } }),
  downloadTemporarySurveyReports: (startDate, endDate, zone) => api.get('/admin/survey-reports/temporary/download', {
    params: { start_date: startDate, end_date: endDate, zone },
    responseType: 'blob'
  }),
  
  // Zone Staff (Electricians & Supervisors)
  getZoneStaff: (category, region) => api.get('/admin/zone-staff', { params: { category, region }, timeout: 0 }),
  createZoneStaff: (data) => api.post('/admin/zone-staff', data),
  updateZoneStaff: (staffId, data) => api.put(`/admin/zone-staff/${staffId}`, data),
  deleteZoneStaff: (staffId) => api.delete(`/admin/zone-staff/${staffId}`),
  uploadZoneStaffExcel: (formData) => api.post('/admin/zone-staff/upload-excel', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 0
  }),
  uploadWhatsAppAttendance: (formData, region) => api.post('/admin/zone-staff/upload-whatsapp', formData, {
    params: { region },
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 0
  }),
  getZoneStaffAttendance: (month, category, region) => api.get('/admin/zone-staff-attendance', { 
    params: { month, category, region },
    timeout: 0 
  }),
  editZoneStaffAttendance: (data) => api.put('/admin/zone-staff-attendance/edit', data),
  downloadZoneStaffAttendance: (month, category, region, format = 'excel') => api.get('/admin/zone-staff-attendance/download', {
    params: { month, category, region, format },
    responseType: 'blob',
    timeout: 0
  }),
  downloadZoneStaffDayAttendance: (date, category, region, format = 'excel') => api.get('/admin/zone-staff-attendance/download-day', {
    params: { date, category, region, format },
    responseType: 'blob',
    timeout: 0
  }),
  getZoneStaffSummary: (date) => api.get('/admin/zone-staff/summary', { params: { date } }),
  
  // Zone Staff Salary Settings
  getZoneStaffSalarySettings: () => api.get('/admin/settings/zone-staff-salary'),
  updateZoneStaffSalarySettings: (data) => api.put('/admin/settings/zone-staff-salary', data),
  
  // Zone Staff Monthly Report
  getZoneStaffMonthlyReport: (month, category, region) => api.get('/admin/zone-staff-monthly-report', {
    params: { month, category, region },
    timeout: 0
  }),
  downloadZoneStaffMonthlyReport: (month, category, region) => api.get('/admin/zone-staff-monthly-report/download', {
    params: { month, category, region },
    responseType: 'blob',
    timeout: 0
  }),
  
  // Attendance Corrections (Admin view of supervisor corrections)
  getAttendanceCorrections: (month, date, supervisorId, zone) => api.get('/admin/attendance-corrections', {
    params: { month, date, supervisor_id: supervisorId, zone },
    timeout: 0
  }),
  downloadAttendanceCorrections: (month, date, supervisorId, zone) => api.get('/admin/attendance-corrections/download', {
    params: { month, date, supervisor_id: supervisorId, zone },
    responseType: 'blob',
    timeout: 0
  }),
  verifyCorrectionToggle: (correctionId) => api.post(`/admin/attendance-corrections/${correctionId}/verify`),
  
  // Correction Permissions (3-day limit admin approval)
  getCorrectionPermissions: () => api.get('/admin/correction-permissions'),
  grantCorrectionPermission: (supervisorId, fromDate, validUntil) => api.post('/admin/correction-permissions', {
    supervisor_id: supervisorId,
    from_date: fromDate,
    valid_until: validUntil
  }),
  revokeCorrectionPermission: (supervisorId) => api.delete(`/admin/correction-permissions/${supervisorId}`),
  getCorrectionPermissionRequests: () => api.get('/admin/correction-permission-requests'),
};

export default api;
